import { useState, useEffect } from "react";
import api, { BACKEND_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { X, Loader2, ImageIcon } from "lucide-react";

/* ============ VISUALS PANEL ============ */
export default function VisualsPanel({ settings, onSave }) {
  const [form, setForm] = useState({
    site_hero_image: "",
    about_banner_image: "",
    site_gallery_images: [],
  });
  const [newGalleryUrl, setNewGalleryUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!settings) return;
    setForm({
      site_hero_image: settings.site_hero_image || "",
      about_banner_image: settings.about_banner_image || "",
      site_gallery_images: Array.isArray(settings.site_gallery_images) ? settings.site_gallery_images : [],
    });
  }, [settings]);

  const handleUploadImage = async (file, target = "gallery") => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = res.data?.url ? (res.data.url.startsWith("http") ? res.data.url : `${BACKEND_URL}${res.data.url}`) : "";
      if (!url) throw new Error("No image url returned");

      if (target === "hero") {
        setForm((f) => ({ ...f, site_hero_image: url }));
      } else if (target === "about") {
        setForm((f) => ({ ...f, about_banner_image: url }));
      } else {
        setForm((f) => ({
          ...f,
          site_gallery_images: [...f.site_gallery_images, url],
        }));
      }
      toast.success("Bild uppladdad");
    } catch {
      toast.error("Kunde inte ladda upp bild");
    } finally {
      setUploading(false);
    }
  };

  const addGalleryUrl = () => {
    const url = newGalleryUrl.trim();
    if (!url) return;
    setForm((f) => ({ ...f, site_gallery_images: [...f.site_gallery_images, url] }));
    setNewGalleryUrl("");
  };

  const removeGalleryImage = (idx) => {
    setForm((f) => ({
      ...f,
      site_gallery_images: f.site_gallery_images.filter((_, i) => i !== idx),
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        site_hero_image: form.site_hero_image || "",
        about_banner_image: form.about_banner_image || "",
        site_gallery_images: form.site_gallery_images,
      };
      const res = await api.put("/admin/settings", payload);
      onSave(res.data);
      toast.success("Visuella inställningar sparade");
    } catch {
      toast.error("Kunde inte spara visuella inställningar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl" data-testid="visuals-panel">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-playfair text-xl font-bold text-[#EDEDED]">Webbplatsens utseende</h2>
          <p className="text-[#E8E8EA] text-xs mt-1">Hantera webbplatsens bilder (ej produktbilder)</p>
        </div>
        <Button onClick={handleSave} disabled={saving || uploading} className="bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5] font-semibold">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Changes"}
        </Button>
      </div>

      <div className="space-y-5">
        <div className="bg-[#121212] rounded-xl border border-[#27272A] p-5 space-y-4">
          <h3 className="text-sm font-semibold text-[#EDEDED] flex items-center gap-2">
            <ImageIcon className="w-4 h-4 text-[#D4B483]" /> Viktiga bilder
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Startsidans hjältebild</Label>
              <Input
                value={form.site_hero_image}
                onChange={(e) => setForm((f) => ({ ...f, site_hero_image: e.target.value }))}
                placeholder="https://..."
                className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] h-10"
              />
              <label className="inline-flex mt-2 cursor-pointer">
                <input type="file" accept="image/*" className="hidden" onChange={(e) => handleUploadImage(e.target.files?.[0], "hero")} />
                <span className="text-xs px-3 py-1.5 rounded-md border border-[#27272A] text-[#D4B483] hover:bg-[#1A1A1A]">{uploading ? "Uploading..." : "Upload Hero"}</span>
              </label>
            </div>

            <div>
              <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Bannerbild för Om oss</Label>
              <Input
                value={form.about_banner_image}
                onChange={(e) => setForm((f) => ({ ...f, about_banner_image: e.target.value }))}
                placeholder="https://..."
                className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] h-10"
              />
              <label className="inline-flex mt-2 cursor-pointer">
                <input type="file" accept="image/*" className="hidden" onChange={(e) => handleUploadImage(e.target.files?.[0], "about")} />
                <span className="text-xs px-3 py-1.5 rounded-md border border-[#27272A] text-[#D4B483] hover:bg-[#1A1A1A]">{uploading ? "Uploading..." : "Upload Banner"}</span>
              </label>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {form.site_hero_image && <img src={form.site_hero_image} alt="Hero" className="w-full h-32 object-cover rounded-lg border border-[#27272A]" />}
            {form.about_banner_image && <img src={form.about_banner_image} alt="About banner" className="w-full h-32 object-cover rounded-lg border border-[#27272A]" />}
          </div>
        </div>

        <div className="bg-[#121212] rounded-xl border border-[#27272A] p-5 space-y-4">
          <h3 className="text-sm font-semibold text-[#EDEDED]">Gallery Images (for About page and future sections)</h3>
          <div className="flex gap-2">
            <Input
              value={newGalleryUrl}
              onChange={(e) => setNewGalleryUrl(e.target.value)}
              placeholder="Lägg till bild-URL"
              className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] h-10"
            />
            <Button type="button" onClick={addGalleryUrl} className="bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5]">Lägg till</Button>
            <label className="inline-flex cursor-pointer">
              <input type="file" accept="image/*" className="hidden" onChange={(e) => handleUploadImage(e.target.files?.[0], "gallery")} />
              <span className="h-10 inline-flex items-center px-3 rounded-md border border-[#27272A] text-[#D4B483] hover:bg-[#1A1A1A]">Ladda upp</span>
            </label>
          </div>

          {form.site_gallery_images.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {form.site_gallery_images.map((url, idx) => (
                <div key={`${url}-${idx}`} className="relative rounded-lg overflow-hidden border border-[#27272A]">
                  <img src={url} alt={`Gallery ${idx + 1}`} className="w-full h-28 object-cover" />
                  <button type="button" onClick={() => removeGalleryImage(idx)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-[#0A0A0A]/90 text-[#EF4444] flex items-center justify-center">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-[#E8E8EA]">Inga galleribilder än.</p>
          )}
        </div>

      </div>
    </div>
  );
}
